// Scherm 11 — de bronnenlijst.
//
// Vorm uit de dummy (V1 r. 129-257): KPI-strook, filterkaart, sorteerrij,
// tabelkaart met kopregel van 34px, paginering, detailpaneel van rechts.
// Kolommen letterlijk uit V1 r. 214.
//
// Het verschil met de dummy is het FILTEREN. V1 biedt drie filters (sector,
// niveau, methode) en gebruikt uitgerekend het slechtst gedekte veld;
// W8 §4 besliste er vijftien. Veertien bestaan als data — "handmatig
// toegevoegd" wordt nergens vastgelegd, dus die staat als reden op het scherm
// in plaats van als filter dat altijd hetzelfde antwoord geeft.
//
// Veertien knoppen naast elkaar is de sliert waar de owner bij de categorieën
// over viel. Daarom vijf brede menu's, elk één VRAAG die je stelt:
//   Wie · Wat voor stuk · Hoe sterk · Wat leverde het op · Vorm
//
// De KPI's boven de lijst tellen sinds deze sessie de WEERGAVE. Ze telden het
// hele corpus terwijl de lijst een filter toonde — dezelfde fout die de owner
// op 2026-08-24 in de trendkop aanwees, één laag hoger.

import { useEffect, useState } from "react";

import { BetaalmuurPil, ONDERBOUWING_LABEL, WiePil, wieTellingUitFacet } from "../components/Bewijs";
import { EmptyList, EmptyReason, LoadError } from "../components/Empty";
import { FilterChips, type Chip } from "../components/FilterChips";
import { FilterMenu } from "../components/FilterMenu";
import { SourceDetail } from "../components/SourceDetail";
import {
  Card,
  FilterBar,
  PageHeader,
  Pager,
  SearchField,
  SlidePanel,
  TableHead,
} from "../components/ui";
import type { ConclusiesResponse, Source, SourceDetailResponse } from "../contracts";
import { api } from "../lib/api";
import { useMeta } from "../lib/meta";
import { gemetenZin, usePropositieFilter } from "../lib/propositieFilter";
import { useDebounced, useResource } from "../lib/useResource";
import { alsLijst, alsTekst, type Voorzet } from "../lib/voorzet";
import { color, radius, size, weight } from "../tokens";

const COLS = "1fr 170px 96px 84px 96px";
const LABELS = ["Bron", "Uitgever", "Autoriteit", "Niveau", "Datum"] as const;
// Methode v3: draagt de lijst het wie-veld, dan heet de kolom "Wie" en staat
// er een pil (instituut · vakblad · bedrijf) in plaats van het gezagsbalkje.
const LABELS_WIE = ["Bron", "Uitgever", "Wie", "Niveau", "Datum"] as const;
const PAGE_SIZE = 20;
// De vaste kolommen (446 px) plus de randen en een leesbare titelkolom.
const TABEL_MIN = 720;

/** Regelafbreking binnen een tooltip. */
const BR = String.fromCharCode(10);

const LEVEL_COLOR: Record<string, string> = {
  Macro: "#111827",
  National: "#2563eb",
  Sector: "#059669",
};

// Machine-waarde -> wat er op het scherm staat. Eén plek per as (backlog 30).
const LEVEL_LABEL: Record<string, string> = {
  Macro: "Macro — internationaal",
  National: "Nationaal — Nederland",
  Sector: "Sector — één bedrijfstak of beroepsgroep",
};
const ROLE_LABEL: Record<string, string> = {
  "trend-primary": "Gelezen als trendbron",
  "skills-primary": "Gelezen op vaardigheden",
  "skills-supportive": "Ondersteunend voor vaardigheden",
  context: "Context — bewaard, niet uitgelezen",
  general: "Algemeen",
  "trend-only": "Alleen gelezen als trendbron",
  arbeidsmarkt: "Alleen gelezen op arbeidsmarktcijfers",
};
const ORIENTATION_LABEL: Record<string, string> = {
  forward_looking: "Kijkt vooruit",
  current_state: "Beschrijft de huidige stand",
  mixed: "Beide",
};
const STERKTE_LABEL: Record<string, string> = {
  sterk: "Sterk (8+)",
  gemiddeld: "Gemiddeld (6-8)",
  zwak: "Zwak (<6)",
};
const JA_NEE_LABEL: Record<string, string> = { ja: "Ja", nee: "Nee" };
const BESTAND_LABEL: Record<string, string> = { pdf: "PDF", webpagina: "Webpagina" };

/**
 * De veertien assen, LOS naast elkaar (owner-review 2026-08-25: "bij Bronnen wil
 * ik alle filters gewoon los hebben, nu is het niet werkbaar").
 *
 * Dit draait de groepering van ronde 4 terug. Die bundelde de assen achter vijf
 * knoppen die elk één vraag stelden (Wie · Wat voor stuk · Hoe sterk · …) om de
 * sliert te vermijden; in de praktijk moest je dan raden achter wélke vraag een
 * as zat. Los is meer knoppen maar minder zoeken — en elk menu houdt wat het in
 * ronde 4 kreeg: een i'tje met uitleg, een zoekregel bij lange assen, gekozen
 * waarden bovenaan en aantallen.
 *
 * De volgorde is die van de vraag die je het eerst stelt: zoeken, waar het over
 * gaat, van wie het komt, wanneer, en dan de eigenschappen.
 */
const ASSEN_VOLGORDE = [
  "uitgeverstype",
  "uitgever",
  "periode",
  "jaar",
  "niveau",
  "leesrol",
  "blikrichting",
  "gezag",
  "sectorrelevantie",
  "trendsignaal",
  "mbosignaal",
  "totaalscore",
  "bewijs",
  "skills",
  "bestandssoort",
] as const;
// LET OP: "jaar" staat hierboven als eigen as. Owner-review ronde 6: "ik wil
// alle filters los getrokken hebben, dus geen samengevoegde filters (zoals
// periode)". Periode bundelde de datumkiezer én het jaarhistogram in één menu;
// dat is precies de vorm die eruit moest.

/** Per as: hoe heet hij op het scherm, en hoe heten zijn waarden. */
const AS: Record<
  string,
  { label: string; uitleg?: string; labels?: Record<string, string>; sorteer?: "waarde" }
> = {
  uitgever: {
    label: "Uitgever",
    uitleg: "Het hoofddomein waaronder de bron is gepubliceerd, met het aantal erbij.",
  },
  uitgeverstype: {
    label: "Uitgeverstype",
    uitleg:
      "Wat voor soort partij dit publiceert, uit config/uitgeverstypen.yaml. " +
      "Gematcht op de volledige host, langste treffer wint — daardoor valt " +
      "cedefop.europa.eu onder Onderwijs en niet onder de europa.eu-hoop.",
  },
  sector: { label: "Sector" },
  periode: { label: "Publicatiedatum" },
  niveau: {
    label: "Niveau",
    uitleg: "Hoe breed de bron kijkt: internationaal, nationaal of één bedrijfstak.",
    labels: LEVEL_LABEL,
  },
  leesrol: {
    label: "Leesrol",
    uitleg: "Met welke bril de pijplijn dit stuk gelezen heeft — dat bepaalt wat eruit kwam.",
    labels: ROLE_LABEL,
  },
  blikrichting: {
    label: "Blikrichting",
    uitleg: "Beschrijft het stuk de huidige stand, kijkt het vooruit, of allebei?",
    labels: ORIENTATION_LABEL,
  },
  // Methode v3: alleen in de balk zodra de API het facet levert.
  onderbouwing: {
    label: "Waarop",
    uitleg:
      "Waarop de bron zijn uitspraak baseert: eigen onderzoek, cijfers van anderen, " +
      "ervaring of casus, mening, wettekst — of onbekend.",
    labels: ONDERBOUWING_LABEL,
  },
  gezag: {
    label: "Gezag",
    uitleg: "Hoe zwaar de uitgever weegt (rubriekdimensie source_credibility, 0-10).",
    labels: STERKTE_LABEL,
  },
  sectorrelevantie: {
    label: "Sectorrelevantie",
    uitleg: "Hoe dicht het stuk bij de sector staat waarvoor we lezen.",
    labels: STERKTE_LABEL,
  },
  trendsignaal: {
    label: "Trendsignaal",
    uitleg: "Hoeveel verandering het stuk beschrijft — de kern van waarom we het lezen.",
    labels: STERKTE_LABEL,
  },
  mbosignaal: {
    label: "Mbo-signaal",
    uitleg: "Hoe bruikbaar het is op mbo-niveau, in plaats van alleen academisch.",
    labels: STERKTE_LABEL,
  },
  totaalscore: {
    label: "Totaalscore",
    uitleg: "De vier cijfers hierboven gewogen samen (config/source-rubric.yaml).",
    labels: STERKTE_LABEL,
  },
  bewijs: {
    label: "Draagt bewijs",
    uitleg: "Staat er een citaat uit deze bron onder een trend?",
    labels: JA_NEE_LABEL,
  },
  skills: {
    label: "Levert vaardigheden",
    uitleg: "Heeft de leespas uit deze bron één of meer vaardigheden gehaald?",
    labels: JA_NEE_LABEL,
  },
  bestandssoort: { label: "Bestandssoort", labels: BESTAND_LABEL },
  jaar: {
    label: "Jaar",
    uitleg:
      "De verdeling over publicatiejaren. Wil je op datum filteren, gebruik dan " +
      "het menu Periode — dat kan ook een half jaar.",
    sorteer: "waarde",
  },
};

const SORTS: { key: string; label: string; hint: string }[] = [
  { key: "gezag", label: "Autoriteit", hint: "Het zwaarst wegende stuk bovenaan." },
  { key: "datum", label: "Datum", hint: "Het nieuwste stuk bovenaan." },
  { key: "uitgever", label: "Uitgever", hint: "Alfabetisch op uitgever." },
];

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("nl-NL", { month: "short", year: "numeric" });
}

/**
 * De publicatiedatum als BEREIK (owner-review ronde 4, Bronnen #4:
 * "publicatiejaar moet met een uitgebreide datum selector, manueel, dit jaar,
 * etc.").
 *
 * Waarom het jaar-menu niet volstond: gemeten loopt het corpus van 2003 tot
 * 2028 — 22 verschillende jaren, dus 22 vinkjes waarvan je er meestal één of
 * twee wilt. Vier veelgebruikte keuzes plus een handmatig bereik zijn sneller
 * en laten bovendien een half jaar toe, wat met vinkjes per jaar niet kan.
 *
 * Het jaar-HISTOGRAM blijft er los naast staan (in het menu Vorm): dat filtert
 * niet maar toont wáár de bronnen zitten, en dat is een andere vraag.
 */

type Periode = { van?: string; tot?: string };

const VANDAAG = () => new Date();
const iso = (d: Date) => d.toISOString().slice(0, 10);
const min = (d: Date, maanden: number) => {
  const kopie = new Date(d);
  kopie.setMonth(kopie.getMonth() - maanden);
  return kopie;
};

const PRESETS: { label: string; uitleg: string; maak: () => Periode }[] = [
  { label: "Alles", uitleg: "Geen beperking op publicatiedatum.", maak: () => ({}) },
  {
    label: "Dit jaar",
    uitleg: "Bronnen met een publicatiedatum in het lopende kalenderjaar.",
    maak: () => ({ van: `${VANDAAG().getFullYear()}-01-01`, tot: undefined }),
  },
  {
    label: "Laatste 12 maanden",
    uitleg: "Vanaf vandaag een jaar terug.",
    maak: () => ({ van: iso(min(VANDAAG(), 12)), tot: undefined }),
  },
  {
    label: "Laatste 3 jaar",
    uitleg: "Vanaf vandaag drie jaar terug.",
    maak: () => ({ van: iso(min(VANDAAG(), 36)), tot: undefined }),
  },
];

function DatumKiezer({
  waarde,
  onChange,
}: {
  waarde: Periode;
  onChange: (p: Periode) => void;
}) {
  const actief = (p: Periode) =>
    (p.van ?? "") === (waarde.van ?? "") && (p.tot ?? "") === (waarde.tot ?? "");
  const veld: React.CSSProperties = {
    width: "100%",
    padding: "6px 9px",
    border: `1px solid ${color.border}`,
    borderRadius: radius.quote,
    fontSize: 12,
    fontFamily: "inherit",
    color: color.body,
    outline: "none",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {PRESETS.map((p) => {
        const gekozen = actief(p.maak());
        return (
          <button
            key={p.label}
            title={p.uitleg}
            onClick={() => onChange(p.maak())}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              width: "100%",
              padding: "8px 10px",
              background: gekozen ? color.surfaceQuote : "transparent",
              border: "none",
              borderRadius: radius.quote,
              color: gekozen ? color.ink : color.body,
              fontSize: size.meta,
              fontFamily: "inherit",
              cursor: "pointer",
              textAlign: "left",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = color.lineFaint)}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = gekozen ? color.surfaceQuote : "transparent")
            }
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 99,
                border: `1.5px solid ${gekozen ? color.ink : color.disabled}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {gekozen && (
                <span
                  style={{ width: 6, height: 6, borderRadius: 99, background: color.ink }}
                />
              )}
            </span>
            {p.label}
          </button>
        );
      })}
      <div
        style={{
          marginTop: 6,
          paddingTop: 8,
          borderTop: `1px solid ${color.lineFaint}`,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 11, color: color.faint }}>Zelf kiezen</span>
        <label style={{ fontSize: 11, color: color.soft }}>
          van
          <input
            type="date"
            style={veld}
            value={waarde.van ?? ""}
            onChange={(e) => onChange({ ...waarde, van: e.target.value || undefined })}
          />
        </label>
        <label style={{ fontSize: 11, color: color.soft }}>
          tot en met
          <input
            type="date"
            style={veld}
            value={waarde.tot ?? ""}
            onChange={(e) => onChange({ ...waarde, tot: e.target.value || undefined })}
          />
        </label>
      </div>
    </div>
  );
}

/** De volledige host uit de url — "cedefop.europa.eu" zegt meer dan "europa.eu". */
function host(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

function Row({ source, onOpen }: { source: Source; onOpen: (s: Source) => void }) {
  const [hover, setHover] = useState(false);
  const zonderTitel = source.title === source.url;
  // De kolom toont het HOOFDdomein, want daarop filtert het menu Uitgever. Maar
  // bij europa.eu zegt dat weinig: daaronder zitten Cedefop (onderwijs), ENISA
  // (beleid) en Eurofound (arbeidsmarktdata). Filter je op Onderwijs, dan wil
  // je kunnen zien wáárom deze rij erbij staat — dus de host in de tooltip.
  const h = host(source.url);
  const anders = h && h !== source.publisher;
  return (
    <div
      onClick={() => onOpen(source)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: COLS,
        alignItems: "center",
        padding: "0 16px",
        minHeight: 44,
        cursor: "pointer",
        background: hover ? color.surfaceAlt : color.surface,
        transition: "background 0.1s",
        borderBottom: `1px solid ${color.lineFaint}`,
      }}
    >
      <span
        title={
          zonderTitel
            ? "Deze bron gaf geen titel mee bij het ophalen; dit is de link."
            : source.title
        }
        style={{
          fontSize: size.meta,
          color: zonderTitel ? color.faint : color.ink,
          fontStyle: zonderTitel ? "italic" : "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingRight: 10,
        }}
      >
        {source.title}
      </span>
      <span
        title={
          anders
            ? `${source.publisher} — dit stuk staat op ${h}`
            : source.publisher
        }
        style={{
          fontSize: 11,
          color: color.soft,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingRight: 10,
        }}
      >
        {source.publisher}
        {anders && (
          <span style={{ color: color.disabled }}> · {h!.replace(`.${source.publisher}`, "")}</span>
        )}
      </span>
      {/* Methode v3: wie het zegt (pil) in plaats van het gezagsbalkje.
          Zonder het veld staat het balkje er zoals voorheen. */}
      {source.wie ? (
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          <WiePil bron={source} />
          <BetaalmuurPil bron={source} />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 40, height: 3, background: color.lineSoft, borderRadius: 99 }}>
            <div
              style={{
                height: "100%",
                width: `${Math.max(0, Math.min(10, source.authority)) * 10}%`,
                background:
                  source.authority >= 9 ? color.ink : source.authority >= 8 ? color.body : color.soft,
                borderRadius: 99,
              }}
            />
          </div>
          <span style={{ fontSize: 11, fontWeight: weight.medium, color: color.body }}>
            {source.authority.toFixed(1)}
          </span>
        </div>
      )}
      {source.level ? (
        <span
          title={LEVEL_LABEL[source.level] ?? source.level}
          style={{
            fontSize: 9,
            fontWeight: weight.bold,
            padding: "2px 5px",
            borderRadius: 3,
            background: LEVEL_COLOR[source.level] ?? color.soft,
            color: "white",
            justifySelf: "start",
            letterSpacing: "0.04em",
          }}
        >
          {source.level.slice(0, 3).toUpperCase()}
        </span>
      ) : (
        <EmptyReason reason="geen leespas" style={{ fontSize: 10 }} />
      )}
      <span style={{ fontSize: 11, color: color.soft }}>{source.date ? fmtDate(source.date) : "datum onbekend"}</span>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: React.ReactNode; sub: React.ReactNode }) {
  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: "12px 16px",
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: weight.bold, color: color.ink, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: color.faint, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

/** Een treffer uit de zoekoverlay: die hoort hier open te gaan.
 *  Vóór owner-review ronde 4 werkte dat alleen voor trends; een skill of bron
 *  aanklikken deed niets. */
export function SourcesPage({
  openen,
  voorzet,
}: {
  openen?: Source | null;
  /** Een filter dat van buiten wordt voorgezet (chat-doorklik, redactiekaart). */
  voorzet?: Voorzet | null;
} = {}) {
  const meta = useMeta();
  const [page, setPage] = useState(1);
  const [gekozen, setGekozen] = useState<Record<string, string[]>>({});
  // Filter Propositie (ADR 2026-10-23-ict-los-platform), zie lib/propositieFilter.
  const prop = usePropositieFilter(meta);
  const [zoek, setZoek] = useState("");
  const [periode, setPeriode] = useState<Periode>({});
  const [sorteer, setSorteer] = useState("gezag");
  const [selected, setSelected] = useState<Source | null>(null);
  const [detail, setDetail] = useState<SourceDetailResponse | null>(null);
  // Eigen route (de conclusielaan raakt router.py niet aan), dus een tweede
  // aanroep bij het openen van het paneel. null = nog aan het laden.
  const [conclusies, setConclusies] = useState<ConclusiesResponse | null>(null);

  // Wacht tot je even stil bent met typen: anders vuurt élke toetsaanslag een
  // ronde naar de database.
  const zoekTraag = useDebounced(zoek, 300);

  // Een treffer uit de zoekoverlay opent hier zijn detailpaneel.
  useEffect(() => {
    if (openen) setSelected(openen);
  }, [openen]);

  // Een voorgezet filter vervangt de hele keuze: kwam je hier via "bekijk alle
  // 49 bronnen", dan hoor je die 49 te zien en niet 49 doorsneden met wat er al
  // aanstond. De assen die de chat kent zijn uitgever, zoekterm en een
  // begindatum (voor "de afgelopen twee maanden").
  useEffect(() => {
    if (!voorzet) return;
    const uitgevers = alsLijst(voorzet, "uitgever");
    setGekozen(uitgevers.length ? { uitgever: uitgevers } : {});
    setZoek(alsTekst(voorzet, "zoek"));
    const van = alsTekst(voorzet, "van");
    setPeriode(van ? { van } : {});
    setPage(1);
  }, [voorzet]);
  // Blok 5 (02-11): bij een propositie die niet ICT is staan vak- én
  // arbeidsmarktbronnen aan, zodat elke bron achter een uitspraak te vinden is.
  // De server blijft zonder keuze op vakbronnen staan; ICT vraagt dus niets extra.
  const gevraagdeSectoren = [...(gekozen.sector ?? []), ...prop.sleutels];
  const metArbeidsmarkt = gevraagdeSectoren.length > 0 && !gevraagdeSectoren.includes("ict");
  const query = {
    ...gekozen,
    ...(prop.sleutels.length ? { sector: gevraagdeSectoren } : {}),
    ...(metArbeidsmarkt && !gekozen.soort ? { soort: ["vak", "arbeidsmarkt"] } : {}),
    ...(zoekTraag ? { search: zoekTraag } : {}),
    ...(periode.van ? { van: periode.van } : {}),
    ...(periode.tot ? { tot: periode.tot } : {}),
  };
  const filterKey = JSON.stringify(query);
  // Methode v3 kent geen sortering op gezag: de lijst opent dan op datum.
  const sorteerEcht = meta?.bronlabels && sorteer === "gezag" ? "datum" : sorteer;
  const list = useResource(
    () => api.sources({ ...query, page, pageSize: PAGE_SIZE, sorteer: sorteerEcht }),
    [page, filterKey, sorteerEcht, meta],
    meta !== null && prop.klaar,
  );

  const facets = list.data?.facets ?? {};
  const items = list.data?.items ?? [];
  // Methode v3: de as "Waarop" (onderbouwing) staat er alleen als de API het
  // facet levert; het wie-veld op de rijen bepaalt de kolomkop.
  const assen: readonly string[] = facets.onderbouwing
    ? ASSEN_VOLGORDE.flatMap((as) => (as === "uitgeverstype" ? [as, "onderbouwing"] : [as]))
    : ASSEN_VOLGORDE;
  const wieKolom = items.some((s) => s.wie !== undefined && s.wie !== null);
  // Methode v3 (wie · waarop): de oude gezag-teksten wijken. Het signaal is
  // /api/meta (bronlabels), met de lijst zelf als tweede getuige. Zonder: alles
  // zoals het was.
  const v3 = Boolean(meta?.bronlabels) || Boolean(facets.onderbouwing) || wieKolom;
  const zichtbareAssen = v3 ? assen.filter((as) => as !== "gezag") : assen;
  const sorts = v3 ? SORTS.filter((s) => s.key !== "gezag") : SORTS;
  const wieTelling = v3 ? wieTellingUitFacet(facets.uitgeverstype, gekozen.uitgeverstype ?? []) : [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const kpis = list.data?.kpis;

  const toekomstig = kpis?.lastUpdated
    ? Date.parse(kpis.lastUpdated) > Date.now()
    : false;

  const sectorLabel = (key: string) =>
    (meta?.sectors ?? []).find((s) => s.key === key)?.label ?? key;
  // De uitgeverstype-as komt uit config/uitgeverstypen.yaml via /api/meta; dit
  // scherm draagt er geen eigen kopie van (backlog 30: één as, één plek).
  const typeLabel = (key: string) =>
    (meta?.uitgeverstypen ?? []).find((t) => t.key === key)?.label ?? key;
  const typenUitleg = (meta?.uitgeverstypen ?? [])
    .map((t) => `${t.label}: ${t.uitleg ?? ""}`.trim())
    .join(BR);

  /** Opties van een as: de waarden die de API terugmeldt, met hun aantal. */
  const optiesVan = (as: string) => {
    const rows = facets[as] ?? [];
    const map = AS[as];
    const gesorteerd =
      map?.sorteer === "waarde" ? [...rows].sort((a, b) => b.value.localeCompare(a.value)) : rows;
    const naam = (v: string) =>
      as === "sector"
        ? sectorLabel(v)
        : as === "uitgeverstype"
          ? typeLabel(v)
          : (map?.labels?.[v] ?? v);
    return {
      options: gesorteerd.map((r) => naam(r.value)),
      counts: Object.fromEntries(gesorteerd.map((r) => [naam(r.value), r.count])),
      terug: Object.fromEntries(gesorteerd.map((r) => [naam(r.value), r.value])),
      heen: Object.fromEntries(gesorteerd.map((r) => [r.value, naam(r.value)])),
    };
  };

  const chips: Chip[] = Object.entries(gekozen).flatMap(([as, waarden]) =>
    (waarden ?? []).map((w) => ({
      axis: AS[as]?.label ?? as,
      value: `${as}::${w}`,
      label: `${AS[as]?.label ?? as}: ${
        as === "sector"
          ? sectorLabel(w)
          : as === "uitgeverstype"
            ? typeLabel(w)
            : (AS[as]?.labels?.[w] ?? w)
      }`,
    })),
  );
  const periodeLabel = periode.van || periode.tot
    ? `Periode: ${periode.van ? fmtDate(periode.van) : "begin"} – ${
        periode.tot ? fmtDate(periode.tot) : "nu"
      }`
    : null;
  const alleChips: Chip[] = [
    ...prop.gekozen.map((v) => ({ axis: "Propositie", value: `propositie::${v}`, label: `Propositie: ${v}` })),
    ...(zoek ? [{ axis: "Zoek", value: "zoek::", label: `Zoekt op "${zoek}"` }] : []),
    ...(periodeLabel ? [{ axis: "Periode", value: "periode::", label: periodeLabel }] : []),
    ...chips,
  ];
  const removeChip = (chip: Chip) => {
    const [as, waarde] = chip.value.split("::");
    if (as === "propositie") {
      prop.zet(prop.gekozen.filter((x) => x !== waarde));
    } else if (as === "zoek") {
      setZoek("");
    } else if (as === "periode") {
      setPeriode({});
    } else {
      setGekozen((g) => ({ ...g, [as!]: (g[as!] ?? []).filter((x) => x !== waarde) }));
    }
    setPage(1);
  };
  const clear = () => {
    setGekozen({});
    prop.terug();
    setZoek("");
    setPeriode({});
    setPage(1);
  };

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let levend = true;
    setDetail(null);
    setConclusies(null);
    api
      .source(selected.id)
      .then((d) => levend && setDetail(d))
      .catch(() => levend && setDetail({ source: selected, otherFromPublisher: [] }));
    api
      .bronConclusies(selected.id)
      .then((c) => levend && setConclusies(c))
      // Een mislukte tweede aanroep mag het paneel niet blokkeren; het blok
      // toont dan zijn eigen reden in plaats van te blijven laden.
      .catch(
        () =>
          levend &&
          setConclusies({
            documentId: selected.id,
            bevindingen: [],
            secties: [],
            reden: "conclusies konden niet worden opgehaald",
          }),
      );
    return () => {
      levend = false;
    };
  }, [selected]);

  return (
    <div>
      <PageHeader
        title="Bronnen"
        count={
          list.loading
            ? "laden…"
            : `${total.toLocaleString("nl-NL")} ${total === 1 ? "bron" : "bronnen"} in beeld` +
              ` · pagina ${page} van ${totalPages}`
        }
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Kpi
          label="Bronnen"
          value={total.toLocaleString("nl-NL")}
          sub="automatisch gepubliceerd"
        />
        {v3 ? (
          // Methode v3: wie het zegt, geteld over deze weergave, in plaats
          // van het gemiddelde gezagscijfer.
          <Kpi
            label="Wie het zegt"
            value={
              wieTelling[0] ? (
                <>
                  {wieTelling[0].aantal.toLocaleString("nl-NL")}{" "}
                  <span style={{ fontSize: 11, fontWeight: weight.regular, color: color.faint }}>
                    {wieTelling[0].woord}
                  </span>
                </>
              ) : (
                "—"
              )
            }
            sub={
              wieTelling.length > 1
                ? `${wieTelling
                    .slice(1)
                    .map((w) => `${w.aantal.toLocaleString("nl-NL")} ${w.woord}`)
                    .join(" · ")} · in deze weergave`
                : "in deze weergave"
            }
          />
        ) : (
          <Kpi
            label="Gem. autoriteit"
            value={kpis ? kpis.avgAuthority.toFixed(1) : "—"}
            sub="van 10, in deze weergave"
          />
        )}
        <Kpi
          label="Laatst bijgewerkt"
          value={fmtDate(kpis?.lastUpdated)}
          sub={
            // Gemeten 2026-08-24: negen gepubliceerde bronnen dragen een datum
            // in de toekomst, uit hun eigen metadata (de EU AI Act-tijdlijn
            // zegt 2028). Het cijfer klopt met de data; het zwijgend
            // wegpoetsen zou de data mooier voordoen dan ze is.
            toekomstig
              ? "de nieuwste datum ligt in de toekomst — zo vermeldt de bron hem zelf"
              : "nieuwste publicatiedatum"
          }
        />
        <Kpi
          label="Sectordekking"
          value={kpis?.sectorCoverage ?? "—"}
          sub={`van ${(meta?.sectors ?? []).length || 16} sectoren`}
        />
        <Kpi
          label="Handmatig"
          value={
            kpis?.manualCount ?? (
              <span style={{ fontSize: 15, color: color.faint, fontWeight: weight.regular }}>
                niet gemeten
              </span>
            )
          }
          sub={
            kpis?.manualCount == null
              ? "geen veld legt vast of een bron met de hand is toegevoegd"
              : "toegevoegd"
          }
        />
      </div>

      <FilterBar chips={<FilterChips chips={alleChips} onRemove={removeChip} onClearAll={clear} />}>
          {/* Zoek en Sector vooraan — owner-review ronde 4, Bronnen #1. De API
              kent `search` al (titel + uitgever). */}
          <SearchField
            value={zoek}
            onChange={(v) => {
              setZoek(v);
              setPage(1);
            }}
            placeholder="Zoek bronnen…"
          />
          <div style={{ width: 1, height: 24, background: color.border, flexShrink: 0 }} />
          {prop.toon && (
            <FilterMenu
              label="Propositie"
              options={prop.opties}
              values={prop.gekozen}
              note="Elk scherm opent op jouw propositie."
              onChange={(v) => {
                prop.zet(v);
                setPage(1);
              }}
            />
          )}
          <FilterMenu
            label="Sector"
            options={optiesVan("sector").options}
            values={(gekozen.sector ?? []).map((v) => optiesVan("sector").heen[v] ?? v)}
            counts={optiesVan("sector").counts}
            note={
              `Over welke sector gaat de bron. ${gemetenZin(meta)}; de andere ` +
              "staan klaar in de configuratie en kleuren mee zodra hun corpus gedraaid is."
            }
            onChange={(v) => {
              const o = optiesVan("sector");
              setGekozen((g) => ({
                ...g,
                sector: v.map((label) => o.terug[label]!).filter(Boolean),
              }));
              setPage(1);
            }}
          />
          {zichtbareAssen.map((as) => {
            // De periode is geen aankruislijst maar een eigen kiezer.
            if (as === "periode") {
              const actief = Boolean(periode.van || periode.tot);
              return (
                <FilterMenu
                  key={as}
                  label={actief ? "Periode (1)" : "Periode"}
                  note={AS[as]?.uitleg}
                  sections={[
                    {
                      key: as,
                      label: "Publicatiedatum",
                      uitleg: AS[as]?.uitleg,
                      options: [],
                      values: actief ? ["gekozen"] : [],
                      onChange: () => setPeriode({}),
                      render: () => (
                        <DatumKiezer
                          waarde={periode}
                          onChange={(p) => {
                            setPeriode(p);
                            setPage(1);
                          }}
                        />
                      ),
                    },
                  ]}
                />
              );
            }
            const o = optiesVan(as);
            const uitleg =
              as === "uitgeverstype"
                ? `${AS[as]?.uitleg ?? ""}${BR}${BR}${typenUitleg}`
                : AS[as]?.uitleg;
            return (
              <FilterMenu
                key={as}
                label={AS[as]?.label ?? as}
                note={uitleg}
                // Breed zodra de as veel waarden heeft (uitgever: 146).
                options={o.options}
                values={(gekozen[as] ?? []).map((v) => o.heen[v] ?? v)}
                counts={o.counts}
                onChange={(v: string[]) => {
                  setGekozen((g) => ({
                    ...g,
                    [as]: v.map((label) => o.terug[label]!).filter(Boolean),
                  }));
                  setPage(1);
                }}
              />
            );
          })}
      </FilterBar>

      {/* Sorteren RECHTS uitgelijnd, zoals de referentie (V1 r. 203-209).
          Owner-review ronde 4, Bronnen #5. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: size.meta, color: color.faint }}>Sorteren op</span>
        {sorts.map((s) => (
          <button
            key={s.key}
            className={"sort-btn" + (sorteerEcht === s.key ? " active" : "")}
            title={s.hint}
            onClick={() => {
              setSorteer(s.key);
              setPage(1);
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <Card
        style={{
          overflow: "hidden",
          // Blijft staan terwijl de nieuwe selectie onderweg is; dimt alleen.
          // Een lijst die bij elke filterklik leegloopt voelt trager dan hij is.
          opacity: list.stale ? 0.5 : 1,
          transition: "opacity 0.15s",
        }}
      >
        {/* Op een smal scherm schuift de TABEL zijwaarts binnen de kaart; de
            pagina zelf niet. Zonder deze minimumbreedte kreeg de titelkolom
            (1fr) op 390 px nul breedte (gemeten 01-10). */}
        <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: TABEL_MIN }}>
        <TableHead cols={COLS} labels={wieKolom ? LABELS_WIE : LABELS} />
        {list.error ? (
          <LoadError reason={list.error} onRetry={list.reload} />
        ) : list.loading && !list.data ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: color.faint }}>laden…</div>
        ) : items.length === 0 ? (
          <EmptyList
            reason={
              alleChips.length > 0
                ? "Geen bronnen met deze filters."
                : "Geen bronnen gevonden — dit is een lege database, geen leeg filter."
            }
            onReset={alleChips.length > 0 ? clear : undefined}
          />
        ) : (
          items.map((s) => <Row key={s.id} source={s} onOpen={setSelected} />)
        )}
        </div>
        </div>
      </Card>

      <Pager page={page} totalPages={totalPages} onPage={setPage} />

      <SlidePanel open={selected !== null} onClose={() => setSelected(null)} terugLabel="Terug naar de lijst">
        {selected && (
          <SourceDetail
            source={detail?.source ?? selected}
            otherFromPublisher={detail?.otherFromPublisher ?? []}
            conclusies={conclusies}
            onClose={() => setSelected(null)}
          />
        )}
      </SlidePanel>
    </div>
  );
}
